import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/use-auth";

const POLL_FG_MS      = 15_000;
const POLL_BG_MS      = 60_000;
const ALARM_REPEAT_MS = 15_000;

function playOrderAlert() {
  try {
    const Ctx =
      window.AudioContext ??
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).webkitAudioContext as typeof AudioContext;
    const ctx = new Ctx();

    const blasts: { t: number; f: number }[] = [
      { t: 0.00, f: 800  },
      { t: 0.22, f: 1047 },
      { t: 0.55, f: 800  },
      { t: 0.77, f: 1047 },
      { t: 1.20, f: 800  },
      { t: 1.42, f: 1047 },
      { t: 1.75, f: 800  },
      { t: 1.97, f: 1047 },
    ];

    blasts.forEach(({ t, f }) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sawtooth";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0,   ctx.currentTime + t);
      gain.gain.linearRampToValueAtTime(1.0, ctx.currentTime + t + 0.03);
      gain.gain.linearRampToValueAtTime(0.7, ctx.currentTime + t + 0.17);
      gain.gain.linearRampToValueAtTime(0,   ctx.currentTime + t + 0.20);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime  + t + 0.22);
    });

    setTimeout(() => void ctx.close(), 3_500);
  } catch { /* AudioContext unavailable */ }
}

interface PendingOrdersContextValue {
  pendingCount: number;
  alertsOn:    boolean;
  setAlertsOn: (on: boolean) => void;
  clearBadge:  (visiblePendingIds?: number[]) => void;
  hasStore:    boolean;
  storesLoaded: boolean;
}

const PendingOrdersContext = createContext<PendingOrdersContextValue>({
  pendingCount: 0,
  alertsOn:    true,
  setAlertsOn: () => {},
  clearBadge:  () => {},
  hasStore:    false,
  storesLoaded: false,
});

export function usePendingOrders() {
  return useContext(PendingOrdersContext);
}

type OrderLite = { id: number; status: string };
type StoreLite = { id: number };

export function PendingOrdersProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const seenIdsRef           = useRef<Set<number>>(new Set());
  const currentPendingIdsRef = useRef<Set<number>>(new Set());
  const storeIdsRef          = useRef<number[]>([]);
  const pollRef              = useRef<ReturnType<typeof setInterval> | null>(null);
  const alarmRef             = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasLoadedRef         = useRef(false);
  const prevUnseenRef        = useRef(0);

  const [unseenCount, setUnseenCount] = useState(0);
  const [alertsOn, setAlertsOn]       = useState(true);
  const [hasStore, setHasStore]       = useState(false);
  const [storesLoaded, setStoresLoaded] = useState(false);

  const alertsOnRef = useRef(alertsOn);
  useEffect(() => { alertsOnRef.current = alertsOn; }, [alertsOn]);

  const stopAlarm = useCallback(() => {
    if (alarmRef.current) { clearInterval(alarmRef.current); alarmRef.current = null; }
  }, []);

  const startAlarm = useCallback(() => {
    if (!alertsOnRef.current) return;
    if (alarmRef.current) return;
    playOrderAlert();
    alarmRef.current = setInterval(() => {
      if (alertsOnRef.current) playOrderAlert();
    }, ALARM_REPEAT_MS);
  }, []);

  useEffect(() => {
    if (!alertsOn) stopAlarm();
  }, [alertsOn, stopAlarm]);

  const recompute = useCallback((pendingIds: Set<number>) => {
    const unseen = [...pendingIds].filter((id) => !seenIdsRef.current.has(id)).length;
    setUnseenCount(unseen);
    if (hasLoadedRef.current && unseen > prevUnseenRef.current) {
      startAlarm();
    } else if (unseen === 0) {
      stopAlarm();
    }
    prevUnseenRef.current = unseen;
    hasLoadedRef.current  = true;
  }, [startAlarm, stopAlarm]);

  const fetchPendingIds = useCallback(async () => {
    const token = localStorage.getItem("ayrshire_token");
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const storesRes  = await fetch("/api/order/my-stores", { headers });
      if (!storesRes.ok) { setStoresLoaded(true); return; }
      const storesData = await storesRes.json() as { items: StoreLite[] };
      storeIdsRef.current = (storesData.items ?? []).map((s) => s.id);
      setHasStore(storeIdsRef.current.length > 0);
      setStoresLoaded(true);

      if (storeIdsRef.current.length === 0) {
        currentPendingIdsRef.current = new Set();
        setUnseenCount(0);
        stopAlarm();
        return;
      }

      const results = await Promise.all(
        storeIdsRef.current.map(async (id) => {
          const res = await fetch(`/api/order/stores/${id}/orders`, { headers });
          if (!res.ok) return [] as number[];
          const d = await res.json() as { items: OrderLite[] };
          return (d.items ?? [])
            .filter((o) => o.status === "pending")
            .map((o) => o.id);
        }),
      );

      const pendingIds = new Set(results.flat());
      currentPendingIdsRef.current = pendingIds;
      recompute(pendingIds);
    } catch { /* silently ignore network errors */ }
  }, [recompute, stopAlarm]);

  const startPoll = useCallback((intervalMs: number) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => { void fetchPendingIds(); }, intervalMs);
  }, [fetchPendingIds]);

  useEffect(() => {
    if (!user) {
      setUnseenCount(0);
      setHasStore(false);
      setStoresLoaded(false);
      storeIdsRef.current           = [];
      seenIdsRef.current            = new Set();
      currentPendingIdsRef.current  = new Set();
      hasLoadedRef.current          = false;
      prevUnseenRef.current         = 0;
      stopAlarm();
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }

    void fetchPendingIds();
    startPoll(document.hidden ? POLL_BG_MS : POLL_FG_MS);

    const handleVisibility = () => {
      if (document.hidden) {
        startPoll(POLL_BG_MS);
      } else {
        void fetchPendingIds();
        startPoll(POLL_FG_MS);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (pollRef.current) clearInterval(pollRef.current);
      stopAlarm();
    };
  }, [user, fetchPendingIds, startPoll, stopAlarm]);

  const clearBadge = useCallback((visiblePendingIds?: number[]) => {
    const baseline = visiblePendingIds != null
      ? new Set(visiblePendingIds)
      : new Set(currentPendingIdsRef.current);
    seenIdsRef.current = baseline;
    if (visiblePendingIds != null) {
      currentPendingIdsRef.current = new Set(visiblePendingIds);
    }
    recompute(currentPendingIdsRef.current);
  }, [recompute]);

  return (
    <PendingOrdersContext.Provider value={{ pendingCount: unseenCount, alertsOn, setAlertsOn, clearBadge, hasStore, storesLoaded }}>
      {children}
    </PendingOrdersContext.Provider>
  );
}
